import React, { useState, useEffect } from 'react';
import {
  Modal,
  Button,
  Space,
  Typography,
  Alert,
  Spin,
  Card,
  Checkbox,
  Tag,
  Input,
  Select,
  Divider,
  message,
  Row,
  Col
} from 'antd';
import {
  RobotOutlined,
  FileTextOutlined,
  ImportOutlined,
  InfoCircleOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined
} from '@ant-design/icons';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../context/LanguageContext';
import { ORGAN_SYSTEM_DISPLAY_NAMES } from '../utils/organSystemConstants';

const { Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;

/**
 * AI Problem List Generator Component
 * Generates comprehensive problem lists using AI based on patient clinical data
 */
const AIProblemListGenerator = ({ 
  visible, 
  onClose, 
  patientId, 
  onProblemsImported 
}) => {
  const { t } = useTranslation();
  const { currentLanguage } = useLanguage();
  
  // Component state
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState(null);
  const [generatedProblems, setGeneratedProblems] = useState([]);
  const [selectedProblems, setSelectedProblems] = useState([]);
  const [additionalContext, setAdditionalContext] = useState('');
  const [selectedLanguage, setSelectedLanguage] = useState(currentLanguage === 'vi' ? 'vietnamese' : 'english');
  const [generationMetadata, setGenerationMetadata] = useState(null);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (visible) {
      setError(null);
      setGeneratedProblems([]);
      setSelectedProblems([]);
      setGenerationMetadata(null);
      setSelectedLanguage(currentLanguage === 'vi' ? 'vietnamese' : 'english');
    }
  }, [visible, currentLanguage]);

  /**
   * Generate problem list using AI
   */
  const generateProblemList = async () => {
    if (!patientId) {
      message.error('Patient ID is required to generate problem list');
      return;
    }

    console.log(`[AIProblemListGenerator] Starting generation for patient: ${patientId}, language: ${selectedLanguage}`);
    setLoading(true);
    setError(null);
    setGeneratedProblems([]);
    setSelectedProblems([]);

    try {
      const requestPayload = {
        language: selectedLanguage
      };

      // Include additional context if provided
      if (additionalContext && additionalContext.trim()) {
        requestPayload.additionalContext = additionalContext.trim();
        console.log(`[AIProblemListGenerator] Including additional context (${additionalContext.trim().length} chars)`);
      }

      const response = await axios.post(`/patients/${patientId}/ai-problem-list`, requestPayload);

      console.log('[AIProblemListGenerator] API response:', response.data);

      if (response.data.status === 'success' && response.data.data.problems) {
        setGeneratedProblems(response.data.data.problems);
        setGenerationMetadata(response.data.data.metadata);
        console.log(`[AIProblemListGenerator] Successfully generated ${response.data.data.problems.length} problems`);

        // Auto-select all problems by default
        setSelectedProblems(response.data.data.problems.map((_, index) => index));
      } else {
        throw new Error(response.data.message || 'Failed to generate problem list');
      }

    } catch (error) {
      console.error('[AIProblemListGenerator] Error generating problem list:', error);
      
      let errorMessage = 'Failed to generate problem list. Please try again.';
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setError(errorMessage);
      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Import selected problems to patient's problem list
   */
  const importSelectedProblems = async () => {
    if (selectedProblems.length === 0) {
      message.warning('Please select at least one problem to import');
      return;
    }

    console.log(`[AIProblemListGenerator] Importing ${selectedProblems.length} problems`);
    setImporting(true);

    try {
      const problemsToImport = selectedProblems.map(index => generatedProblems[index]);
      
      const response = await axios.post(`/patients/${patientId}/ai-problem-list/import`, {
        selectedProblems: problemsToImport
      });

      console.log('[AIProblemListGenerator] Import response:', response.data);

      if (response.data.status === 'success') {
        const { importedCount, totalSelected, errors } = response.data.data;
        
        if (errors && errors.length > 0) {
          message.warning(`Imported ${importedCount} of ${totalSelected} problems. Some problems had errors.`);
          console.warn('Import errors:', errors);
        } else {
          message.success(`Successfully imported ${importedCount} problems to the patient's problem list`);
        }

        // Notify parent component
        if (onProblemsImported) {
          onProblemsImported(response.data.data.importedProblems);
        }

        // Close modal after successful import
        onClose();
      } else {
        throw new Error(response.data.message || 'Failed to import problems');
      }

    } catch (error) {
      console.error('[AIProblemListGenerator] Error importing problems:', error);
      
      let errorMessage = 'Failed to import problems. Please try again.';
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }
      
      message.error(errorMessage);
    } finally {
      setImporting(false);
    }
  };

  /**
   * Handle problem selection change
   */
  const handleProblemSelection = (checkedValues) => {
    setSelectedProblems(checkedValues);
  };

  /**
   * Select all problems
   */
  const selectAllProblems = () => {
    setSelectedProblems(generatedProblems.map((_, index) => index));
  };

  /**
   * Deselect all problems
   */
  const deselectAllProblems = () => {
    setSelectedProblems([]);
  };

  /**
   * Get organ system color for tags
   */
  const getOrganSystemColor = (system) => {
    const colors = {
      neurological: 'purple',
      cardiovascular: 'red',
      respiratory: 'blue',
      metabolic: 'orange',
      filtration: 'cyan',
      musculoskeletal: 'green',
      immune: 'magenta'
    };
    return colors[system] || 'default';
  };

  /**
   * Get priority color for tags
   */
  const getPriorityColor = (priority) => {
    const colors = {
      Critical: 'red',
      High: 'orange',
      Medium: 'blue',
      Low: 'green'
    };
    return colors[priority] || 'default';
  };

  return (
    <Modal
      title={
        <Space>
          <RobotOutlined />
          <span>{t('patientChart.problemList.aiGeneration.title')}</span>
        </Space>
      }
      open={visible}
      onCancel={onClose}
      width={1000}
      footer={null}
      destroyOnClose
    >
      <div style={{ maxHeight: '70vh', overflowY: 'auto' }}>
        {/* Configuration Section */}
        {generatedProblems.length === 0 && (
          <Card style={{ marginBottom: 16 }}>
            <Space direction="vertical" style={{ width: '100%' }}>
              <div>
                <Text strong>
                  {t('patientChart.problemList.aiGeneration.generationConfig')}
                </Text>
              </div>

              <div>
                <Text>
                  {t('patientChart.problemList.aiGeneration.language')}:
                </Text>
                <Select
                  value={selectedLanguage}
                  onChange={setSelectedLanguage}
                  style={{ width: '100%', marginTop: 4, marginBottom: 16 }}
                >
                  <Option value="english">English</Option>
                  <Option value="vietnamese">Tiếng Việt</Option>
                </Select>
              </div>

              <div>
                <Text>
                  {t('patientChart.problemList.aiGeneration.additionalContext')}:
                </Text>
                <TextArea
                  value={additionalContext}
                  onChange={(e) => setAdditionalContext(e.target.value)}
                  placeholder={t('patientChart.problemList.aiGeneration.additionalContextPlaceholder')}
                  rows={3}
                  maxLength={500}
                  showCount
                  style={{ marginTop: 4 }}
                />
              </div>

              {additionalContext && additionalContext.trim() && (
                <div style={{
                  background: '#e6f7ff',
                  border: '1px solid #91d5ff',
                  borderRadius: '4px',
                  padding: '4px 8px',
                  fontSize: '12px',
                  color: '#1890ff'
                }}>
                  <InfoCircleOutlined style={{ marginRight: 4 }} />
                  {t('patientChart.problemList.aiGeneration.includingContext')} ({additionalContext.trim().length} {selectedLanguage === 'vietnamese' ? 'ký tự' : 'chars'})
                </div>
              )}

              <Button
                type="primary"
                size="large"
                icon={<FileTextOutlined />}
                onClick={generateProblemList}
                loading={loading}
                block
              >
                {t('patientChart.problemList.aiGeneration.generateButton')}
              </Button>
            </Space>
          </Card>
        )}

        {/* Error Display */}
        {error && (
          <Alert
            message={selectedLanguage === 'vietnamese' ? 'Lỗi' : 'Error'}
            description={error}
            type="error"
            showIcon
            style={{ marginBottom: 16 }}
            action={
              <Button size="small" onClick={generateProblemList}>
                {selectedLanguage === 'vietnamese' ? 'Thử lại' : 'Retry'}
              </Button>
            }
          />
        )}

        {/* Loading State */}
        {loading && (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <Spin size="large" />
            <div style={{ marginTop: 16 }}>
              <Text>
                {selectedLanguage === 'vietnamese' 
                  ? 'Đang phân tích dữ liệu lâm sàng và tạo danh sách vấn đề...' 
                  : 'Analyzing clinical data and generating problem list...'}
              </Text>
            </div>
          </div>
        )}

        {/* Generated Problems Display */}
        {generatedProblems.length > 0 && (
          <div>
            {/* Generation Metadata */}
            {generationMetadata && (
              <Alert
                message={
                  <Space>
                    <CheckCircleOutlined />
                    <span>
                      {selectedLanguage === 'vietnamese'
                        ? `Đã tạo thành công ${generationMetadata.totalProblems} vấn đề`
                        : `Successfully generated ${generationMetadata.totalProblems} problems`}
                    </span>
                    {generationMetadata?.generationTimeMs && (
                      <Tag color={generationMetadata.generationTimeMs < 15000 ? 'green' : generationMetadata.generationTimeMs < 30000 ? 'orange' : 'red'}>
                        {Math.round(generationMetadata.generationTimeMs / 1000)}s
                      </Tag>
                    )}
                  </Space>
                }
                description={
                  <div>
                    <Text type="secondary">
                      {selectedLanguage === 'vietnamese'
                        ? `Hệ thống cơ quan được sử dụng: ${generationMetadata.organSystemsUsed?.map(s => ORGAN_SYSTEM_DISPLAY_NAMES[s]).join(', ')}`
                        : `Organ systems used: ${generationMetadata.organSystemsUsed?.map(s => ORGAN_SYSTEM_DISPLAY_NAMES[s]).join(', ')}`}
                    </Text>
                    {generationMetadata?.generationTimeMs && (
                      <div style={{ marginTop: 4 }}>
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                          {selectedLanguage === 'vietnamese'
                            ? `Thời gian tạo: ${Math.round(generationMetadata.generationTimeMs / 1000)}s`
                            : `Generation time: ${Math.round(generationMetadata.generationTimeMs / 1000)}s`}
                        </Text>
                      </div>
                    )}
                  </div>
                }
                type="success"
                showIcon
                style={{ marginBottom: 16 }}
              />
            )}

            {/* Selection Controls */}
            <Card size="small" style={{ marginBottom: 16 }}>
              <Row justify="space-between" align="middle">
                <Col>
                  <Space>
                    <Text strong>
                      {selectedLanguage === 'vietnamese' 
                        ? `Đã chọn: ${selectedProblems.length}/${generatedProblems.length}`
                        : `Selected: ${selectedProblems.length}/${generatedProblems.length}`}
                    </Text>
                    <Button size="small" type="link" onClick={selectAllProblems}>
                      {selectedLanguage === 'vietnamese' ? 'Chọn tất cả' : 'Select All'}
                    </Button>
                    <Button size="small" type="link" onClick={deselectAllProblems}>
                      {selectedLanguage === 'vietnamese' ? 'Bỏ chọn tất cả' : 'Deselect All'}
                    </Button>
                  </Space>
                </Col>
                <Col>
                  <Button
                    type="primary"
                    icon={<ImportOutlined />}
                    onClick={importSelectedProblems}
                    loading={importing}
                    disabled={selectedProblems.length === 0}
                  >
                    {selectedLanguage === 'vietnamese' ? 'Nhập Vào Danh Sách' : 'Import Selected'}
                  </Button>
                </Col>
              </Row>
            </Card>

            {/* Problems List */}
            <Checkbox.Group
              value={selectedProblems}
              onChange={handleProblemSelection}
              style={{ width: '100%' }}
            >
              <Space direction="vertical" style={{ width: '100%' }}>
                {generatedProblems.map((problem, index) => (
                  <Card
                    key={index}
                    size="small"
                    style={{
                      border: selectedProblems.includes(index) ? '2px solid #1890ff' : '1px solid #d9d9d9'
                    }}
                  >
                    <Checkbox value={index} style={{ marginBottom: 8 }}>
                      <Text strong>{problem.problemName}</Text>
                    </Checkbox>
                    
                    {problem.patientFriendlyName && problem.patientFriendlyName !== problem.problemName && (
                      <div style={{ marginLeft: 24, marginBottom: 8 }}>
                        <Text type="secondary" italic>
                          {selectedLanguage === 'vietnamese' ? 'Tên thân thiện: ' : 'Patient-friendly: '}
                          {problem.patientFriendlyName}
                        </Text>
                      </div>
                    )}

                    <div style={{ marginLeft: 24, marginBottom: 8 }}>
                      <Space wrap>
                        <Tag color={getPriorityColor(problem.priority)}>
                          {selectedLanguage === 'vietnamese' ? 'Ưu tiên: ' : 'Priority: '}{problem.priority}
                        </Tag>
                        {problem.organSystems?.map(system => (
                          <Tag key={system} color={getOrganSystemColor(system)}>
                            {ORGAN_SYSTEM_DISPLAY_NAMES[system]}
                          </Tag>
                        ))}
                        {problem.icdCode && (
                          <Tag color="default">ICD-10: {problem.icdCode}</Tag>
                        )}
                      </Space>
                    </div>

                    {problem.rationale && (
                      <div style={{ marginLeft: 24 }}>
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                          <ExclamationCircleOutlined style={{ marginRight: 4 }} />
                          {problem.rationale}
                        </Text>
                      </div>
                    )}
                  </Card>
                ))}
              </Space>
            </Checkbox.Group>

            <Divider />

            {/* Action Buttons */}
            <Row justify="space-between">
              <Col>
                <Button onClick={() => {
                  setGeneratedProblems([]);
                  setSelectedProblems([]);
                  setGenerationMetadata(null);
                }}>
                  {selectedLanguage === 'vietnamese' ? 'Tạo lại' : 'Generate New'}
                </Button>
              </Col>
              <Col>
                <Space>
                  <Button onClick={onClose}>
                    {selectedLanguage === 'vietnamese' ? 'Đóng' : 'Close'}
                  </Button>
                  <Button
                    type="primary"
                    icon={<ImportOutlined />}
                    onClick={importSelectedProblems}
                    loading={importing}
                    disabled={selectedProblems.length === 0}
                  >
                    {selectedLanguage === 'vietnamese' ? 'Nhập Vào Danh Sách' : 'Import Selected'}
                  </Button>
                </Space>
              </Col>
            </Row>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default AIProblemListGenerator;
